import type {
  RealtimeAuthInput,
  RealtimeAuthResponse,
  RealtimeConfig,
  RealtimeEventName,
  RealtimeEvents,
} from '@nexo/types';
import Pusher, { type Channel as PusherChannel } from 'pusher-js';

export interface RealtimeClientOptions {
  config: Extract<RealtimeConfig, { enabled: true }>;
  /** Asks our API to sign a private/presence channel subscription. */
  authorize: (input: RealtimeAuthInput) => Promise<RealtimeAuthResponse>;
}

/** Pusher-protocol client (EventSocket). Host/port come from the API, never hardcoded. */
export function createRealtimeClient({ config, authorize }: RealtimeClientOptions): Pusher {
  return new Pusher(config.key, {
    cluster: 'default', // required by the type, ignored by EventSocket
    wsHost: config.wsHost,
    wsPort: config.wsPort,
    wssPort: config.wsPort,
    forceTLS: config.forceTLS,
    enabledTransports: ['ws', 'wss'],
    disableStats: true,
    channelAuthorization: {
      transport: 'ajax',
      endpoint: '',
      customHandler: ({ socketId, channelName }, callback) => {
        authorize({ socketId, channelName }).then(
          (response) => callback(null, response),
          (error: unknown) =>
            callback(error instanceof Error ? error : new Error(String(error)), null),
        );
      },
    },
  });
}

/** Type-safe `channel.bind`. Returns an unbind function. */
export function bindEvent<E extends RealtimeEventName>(
  channel: PusherChannel,
  event: E,
  handler: (payload: RealtimeEvents[E]) => void,
): () => void {
  const listener = (payload: RealtimeEvents[E]) => handler(payload);
  channel.bind(event, listener);
  return () => channel.unbind(event, listener);
}

export type { Pusher, PusherChannel };
